import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Input, Badge, StatCard, EmptyState, Avatar } from "@realtyflows/ui";
import {
  listMyOrgs,
  createOrganization,
  getOrganization,
  getOrgTeams,
  getOrgFolders,
  getOrgPerformance,
  attachTeamToOrg,
} from "../api";
import useResponsive from "../hooks/useResponsive";

/**
 * OrganizationDashboard — V5 Features 5.5 + 5.6 + 5.7 UI
 *
 * For a franchise / multi-office brokerage owner. Shows:
 *   - Cross-office performance leaderboard
 *   - All teams in the org's reach
 *   - Cross-office folder list (read-only audit)
 *   - Royalty configuration (5.7)
 *
 * Solo agents or single-team brokers see the "Create Organization" CTA.
 */
export default function OrganizationDashboard({ user }) {
  const navigate = useNavigate();
  const responsive = useResponsive();

  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [teams, setTeams] = useState([]);
  const [folders, setFolders] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [tab, setTab] = useState("performance");
  const [loading, setLoading] = useState(true);

  // Create-org form state
  const [showCreate, setShowCreate] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [royaltyPct, setRoyaltyPct] = useState("");
  const [royaltyEmail, setRoyaltyEmail] = useState("");
  const [parentOrgId, setParentOrgId] = useState("");

  // Attach-team form state
  const [attachTeamId, setAttachTeamId] = useState("");

  // Load org list once
  useEffect(() => {
    listMyOrgs()
      .then((list) => {
        setOrgs(list);
        if (list.length > 0 && !selectedOrgId) setSelectedOrgId(list[0].id);
      })
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, []);

  // Load detail whenever selectedOrgId changes
  const loadOrg = useCallback(async (id) => {
    if (!id) return;
    try {
      const [detail, teamsList, perf] = await Promise.all([
        getOrganization(id),
        getOrgTeams(id),
        getOrgPerformance(id).catch(() => null),
      ]);
      setOrgDetail(detail);
      setTeams(teamsList);
      setPerformance(perf);
    } catch (err) {
      console.error(err);
    }
  }, []);
  useEffect(() => { if (selectedOrgId) loadOrg(selectedOrgId); }, [selectedOrgId, loadOrg]);

  // Folders tab lazy-loads
  useEffect(() => {
    if (tab !== "folders" || !selectedOrgId) return;
    getOrgFolders(selectedOrgId, { limit: 200 })
      .then(setFolders)
      .catch(() => setFolders([]));
  }, [tab, selectedOrgId]);

  const handleCreateOrg = async () => {
    if (!orgName.trim()) { window.alert("Name is required"); return; }
    try {
      const created = await createOrganization({
        name: orgName.trim(),
        parent_org_id: parentOrgId ? parseInt(parentOrgId) : null,
        royalty_rate_bps: royaltyPct ? Math.round(parseFloat(royaltyPct) * 100) : null,
        royalty_recipient_email: royaltyEmail || null,
      });
      setOrgs([...orgs, created]);
      setSelectedOrgId(created.id);
      setShowCreate(false);
      setOrgName(""); setRoyaltyPct(""); setRoyaltyEmail(""); setParentOrgId("");
    } catch (err) {
      window.alert(err.message || "Could not create");
    }
  };

  const handleAttachTeam = async () => {
    if (!attachTeamId.trim()) return;
    try {
      await attachTeamToOrg(selectedOrgId, parseInt(attachTeamId));
      setAttachTeamId("");
      loadOrg(selectedOrgId);
    } catch (err) {
      window.alert(err.message || "Could not attach team");
    }
  };

  const fmtMoney = (n) => {
    if (n == null || n === 0) return "$0";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n)}`;
  };
  const fmtRelative = (iso) => {
    if (!iso) return "Never";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  // Empty state — no orgs yet
  if (orgs.length === 0 && !showCreate) {
    return (
      <Panel padding={32} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 24, marginBottom: 14 }}>🏢</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
          Set up your Organization
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20, fontFamily: font.body, lineHeight: 1.6 }}>
          Manage multiple offices under one franchise. Roll up performance across regions, apply franchise royalties automatically, and give the parent org read-only visibility into every branch.
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>Create your first organization</Button>
      </Panel>
    );
  }

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Org switcher + create button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedOrgId(o.id)}
              style={{
                padding: "6px 14px", borderRadius: 99,
                background: o.id === selectedOrgId ? t.textPrimary : t.surfaceWarm,
                color: o.id === selectedOrgId ? t.surface : t.textSecondary,
                border: `1px solid ${o.id === selectedOrgId ? t.textPrimary : t.border}`,
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: font.body,
              }}
            >
              {o.name}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowCreate(true)}>+ New organization</Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <Panel padding={20} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            New organization
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input placeholder="Organization name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
            <Input placeholder="Royalty %" value={royaltyPct} onChange={(e) => setRoyaltyPct(e.target.value)} type="number" />
            <Input placeholder="Royalty recipient email" value={royaltyEmail} onChange={(e) => setRoyaltyEmail(e.target.value)} />
            <select
              value={parentOrgId}
              onChange={(e) => setParentOrgId(e.target.value)}
              style={{ padding: "8px 10px", fontSize: 13, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, fontFamily: font.body, outline: "none" }}
            >
              <option value="">No parent (top-level)</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>Parent: {o.name}</option>)}
            </select>
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button variant="primary" onClick={handleCreateOrg}>Create</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </Panel>
      )}

      {orgDetail && (
        <>
          {/* Header */}
          <Panel padding={20} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>
                  {orgDetail.name}
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
                  {teams.length} office{teams.length === 1 ? "" : "s"} ·{" "}
                  {orgDetail.royalty_rate_bps != null
                    ? `Royalty: ${(orgDetail.royalty_rate_bps / 100).toFixed(2)}%`
                    : "No royalty configured"}
                </div>
                {orgDetail.ancestors && orgDetail.ancestors.length > 0 && (
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6, fontFamily: font.mono }}>
                    Parent chain: {orgDetail.ancestors.map((a) => a.name).join(" → ")}
                  </div>
                )}
              </div>
            </div>
          </Panel>

          {/* Stats row */}
          {performance && (
            <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 14, marginBottom: 16 }}>
              <StatCard label="Offices" value={String(performance.byOffice.length)} delay="0s" />
              <StatCard label="Total GCI (90d)" value={fmtMoney(performance.totalGci)} delay="0.07s" />
              <StatCard label="Total agents" value={String(performance.byOffice.reduce((s, o) => s + o.agentCount, 0))} delay="0.14s" />
              <StatCard label="Total open folders" value={String(performance.byOffice.reduce((s, o) => s + o.openFolders, 0))} delay="0.21s" />
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: 0, marginBottom: 16, background: t.pageBg, borderRadius: 10, padding: 3, border: `1px solid ${t.border}` }}>
            {[
              { id: "performance", label: "Performance" },
              { id: "offices", label: `Offices (${teams.length})` },
              { id: "folders", label: "All Folders" },
            ].map((tb) => (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                style={{
                  flex: 1, padding: "10px 16px", textAlign: "center", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, fontFamily: font.body,
                  cursor: "pointer", transition: "all 0.2s", border: "none",
                  background: tab === tb.id ? t.surface : "transparent",
                  color: tab === tb.id ? t.textPrimary : t.textMuted,
                  boxShadow: tab === tb.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                {tb.label}
              </button>
            ))}
          </div>

          {/* Performance tab */}
          {tab === "performance" && performance && (
            <Panel padding={20}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
                Office Leaderboard — Last 90 Days
              </div>
              {performance.byOffice.length === 0 ? (
                <EmptyState title="No offices yet" sub="Attach a team to this organization to start tracking performance." />
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: font.body }}>
                  <thead>
                    <tr style={{ background: t.surfaceWarm, color: t.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {["Office", "Agents", "Deals Closed", "GCI", "Open Folders"].map((h) => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${t.border}`, fontFamily: font.mono, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {performance.byOffice.map((o) => (
                      <tr key={o.teamId} style={{ borderBottom: `1px solid ${t.border}` }}>
                        <td style={{ padding: 10, color: t.textPrimary, fontWeight: 600 }}>
                          {o.officeName || o.teamName}
                        </td>
                        <td style={{ padding: 10, fontFamily: font.mono }}>{o.agentCount}</td>
                        <td style={{ padding: 10, fontFamily: font.mono, fontWeight: 700 }}>{o.dealsClosed90d}</td>
                        <td style={{ padding: 10, fontFamily: font.mono, fontWeight: 700 }}>{fmtMoney(o.gci90d)}</td>
                        <td style={{ padding: 10, fontFamily: font.mono }}>{o.openFolders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          )}

          {/* Offices tab */}
          {tab === "offices" && (
            <Panel padding={20}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Offices</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input placeholder="Team ID to attach" value={attachTeamId} onChange={(e) => setAttachTeamId(e.target.value)} />
                  <Button variant="primary" size="sm" onClick={handleAttachTeam} disabled={!attachTeamId.trim()}>Attach</Button>
                </div>
              </div>
              {teams.length === 0 ? (
                <EmptyState title="No offices yet" sub="Enter a Team ID above to attach an existing team to this org." />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {teams.map((tm) => (
                    <div key={tm.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: 10, background: t.surfaceWarm,
                      border: `1px solid ${t.border}`, borderRadius: 8,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                          {tm.office_name || tm.name}
                        </div>
                        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                          {tm.office_address || tm.team_plan || "Office"}
                        </div>
                      </div>
                      <Badge color={t.sky}>{tm.member_count || 0} members</Badge>
                      <Badge color={t.amber}>{tm.open_folders || 0} open folders</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}

          {/* Folders tab — cross-office read-only audit view */}
          {tab === "folders" && (
            <Panel padding={20}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
                All Open Folders — Cross-Office
              </div>
              {folders.length === 0 ? (
                <EmptyState title="No folders" sub="Folders from every office in this org will appear here as they're created." />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {folders.map((f) => (
                    <div key={f.id}
                      onClick={() => navigate(`/contracts/folders/${f.id}`)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: 10, background: t.surfaceWarm,
                        border: `1px solid ${t.border}`, borderRadius: 8, cursor: "pointer",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.name}
                        </div>
                        <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                          {f.team_name || `Team #${f.team_id}`} · {f.agent_name || "—"} · {fmtRelative(f.last_activity_at)}
                        </div>
                      </div>
                      {parseInt(f.missing_required) > 0 && <Badge color={t.coral}>{f.missing_required} missing</Badge>}
                      <Badge color={t.sky}>{f.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
